import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import ButtonCTA from '@/components/base/ButtonCTA';
import ButtonWhatsApp from '@/components/base/ButtonWhatsApp';
import ButtonPhone from '@/components/base/ButtonPhone';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import PriceTable from '@/components/feature/PriceTable';
import FAQAccordion from '@/components/feature/FAQAccordion';
import CTABlock from '@/components/feature/CTABlock';
import ContactFormBlock from '@/components/feature/ContactFormBlock';
import RevealWrapper from '@/components/base/RevealWrapper';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { REASSURANCE_ITEMS } from '@/mocks/homeData';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { localBusiness, website, service, faqPage, breadcrumb, article } from '@/lib/seo';

const PRICING_ROWS = [
  { service: 'Traitement insectes rampants (ravets, fourmis, scolopendres)', price: '100 € – 280 €' },
  { service: 'Destruction nid de guêpes', price: '90 € – 180 €' },
  { service: 'Punaises de lit (protocole complet)', price: '90 € – 350 €' },
  { service: 'Contrat annuel professionnel (HACCP)', price: 'Sur devis' },
];

const FAQ_ITEMS = [
  {
    question: 'Comment savoir quel insecte m\'envahit ?',
    answer: 'Envoyez une photo (même floue) ou une description sur WhatsApp : nos techniciens identifient l\'espèce et vous annoncent le traitement et le prix en 2 h.',
  },
  {
    question: 'Intervenez-vous partout en Guadeloupe ?',
    answer: 'Oui, sur toute l\'île — Grande-Terre, Basse-Terre et leurs communes. À domicile et en entreprise, sous 24-48 h.',
  },
  {
    question: 'Vos produits sont-ils dangereux ?',
    answer: 'Ce sont des produits professionnels appliqués par des techniciens formés (Certibiocide), aux bons endroits et aux bonnes doses. Les consignes (délai avant réoccupation d\'une pièce, animaux) sont systématiquement expliquées.',
  },
  {
    question: 'Traitez-vous les fourmis-folles ?',
    answer: 'Oui — ces petites fourmis erratiques qui colonisent prises, climatiseurs et appareils électriques demandent un gel appât spécifique. C\'est une intervention courante ici.',
  },
  {
    question: 'Peut-on grouper plusieurs traitements ?',
    answer: 'Oui, et c\'est recommandé : ravets + scolopendres + fourmis partagent les mêmes points d\'entrée. Tarif groupé sur devis.',
  },
];

const INSECT_TREATMENTS = [
  {
    title: 'Ravets et cafards',
    description: 'Le fléau n°1 des cuisines guadeloupéennes. Gel appât professionnel à effet domino, traitement des points d\'entrée.',
    link: '/traitement-ravets-cafards-guadeloupe',
    icon: 'ri-bug-line',
  },
  {
    title: 'Punaises de lit',
    description: 'En progression avec le tourisme. Traitement complet chambre par chambre, 2 à 3 passages, discrétion garantie.',
    link: '/punaises-de-lit-guadeloupe',
    icon: 'ri-moon-clear-line',
  },
  {
    title: 'Scolopendres',
    description: 'La barrière périmétrique qui les arrête avant la porte.',
    link: '/scolopendres-guadeloupe',
    icon: 'ri-arrow-go-back-line',
  },
  {
    title: 'Moustiques',
    description: 'Traitement des jardins, gîtes larvaires et zones de repos.',
    link: '/demoustication-guadeloupe',
    icon: 'ri-magic-line',
  },
  {
    title: 'Fourmis',
    description: 'Colonnes dans la cuisine, fourmis-folles dans les prises et appareils électriques, fourmilières au jardin : gel appât ciblé qui remonte jusqu\'à la reine (la seule méthode durable — écraser la colonne visible ne sert à rien).',
    link: '/desinsectisation-guadeloupe',
    icon: 'ri-route-line',
  },
  {
    title: 'Guêpes et abeilles',
    description: 'Destruction de nids de guêpes sous toitures, dans les arbres ou les murs, avec équipement de protection. Pour les abeilles, nous privilégions systématiquement le déplacement par un apiculteur partenaire quand c\'est possible.',
    link: '/desinsectisation-guadeloupe',
    icon: 'ri-alert-line',
  },
  {
    title: 'Mouches, mites, puces',
    description: 'Infestations de mouches (élevages voisins, poubelles collectives), mites alimentaires, puces après passage d\'animaux : traitements ciblés en un passage.',
    link: '/desinsectisation-guadeloupe',
    icon: 'ri-close-circle-line',
  },
];

export default function Desinsectisation() {
  useDocumentHead({
    title: 'Désinsectisation Guadeloupe : tous insectes, toute l\'île',
    description: 'Ravets, fourmis, punaises, moustiques, guêpes : désinsectisation professionnelle partout en Guadeloupe. Produits certifiés, prix ferme, intervention 24-48 h.',
    jsonLd: [service({ name: 'Désinsectisation en Guadeloupe', serviceType: 'Désinsectisation', path: '/desinsectisation-guadeloupe', description: 'Traitement de tous les insectes nuisibles à domicile en Guadeloupe.' }), faqPage(FAQ_ITEMS), breadcrumb([{ name: 'Accueil', path: '/' }, { name: 'Désinsectisation', path: '/desinsectisation-guadeloupe' }])],
  });

  return (
    <main>
      <Header />
      <Breadcrumb items={[{label:'Accueil',href:'/'},{label:'Désinsectisation'}]} />

      {/* HERO */}
      <section className="relative bg-primary-500 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/desinsectisation-1.webp"
            alt="Technicien en tenue de protection appliquant un insecticide professionnel dans une maison créole en Guadeloupe"
            className="w-full h-full object-cover object-top"
           loading="eager" fetchPriority="high" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(11,58,46,0.94), rgba(11,58,46,0.74) 52%, rgba(11,58,46,0.42))' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(11,58,46,0.55), transparent 38%, transparent 82%, rgba(11,58,46,0.38))' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <h1 className="font-heading font-black text-3xl md:text-5xl text-white leading-tight mb-6">
              Désinsectisation en Guadeloupe : quel que soit l&rsquo;insecte, on a le traitement
            </h1>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">
              Sous notre climat, les insectes ne prennent jamais de vacances. Ravets qui remontent des égouts en saison des pluies, fourmis qui envahissent la cuisine, punaises de lit rapportées d&rsquo;un voyage, guêpes sous la toiture, moustiques qui rendent la terrasse invivable : Gwada Nuisibles traite tous les insectes nuisibles, partout en Guadeloupe, avec des produits professionnels certifiés Certibiocide.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4 flex-wrap">
              <ButtonCTA>Devis gratuit</ButtonCTA>
              <ButtonWhatsApp>WhatsApp direct</ButtonWhatsApp>
              <ButtonPhone />
            </div>
          </div>
        </div>
      </section>

      {/* RÉASSURANCE */}
      <ReassuranceBar items={REASSURANCE_ITEMS} />

      {/* NOS TRAITEMENTS PAR INSECTE */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-10 text-center">
              Nos traitements par insecte
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
              {INSECT_TREATMENTS.map((item) => (
                <Link
                  key={item.title}
                  to={item.link}
                  className="bg-white rounded-xl p-6 border border-background-200/70 hover:border-secondary-300 hover:shadow-sm transition-all cursor-pointer group flex flex-col"
                >
                  <div className="w-11 h-11 mb-4 flex items-center justify-center rounded-full bg-secondary-100 group-hover:bg-secondary-200 transition-colors">
                    <i className={`${item.icon} text-secondary-500 text-lg w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-base text-foreground-950 mb-2 group-hover:text-secondary-500 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-foreground-600 text-sm leading-relaxed flex-1">
                    {item.description}
                  </p>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-secondary-500 font-semibold text-sm whitespace-nowrap">
                    Voir le traitement
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* COMMENT ÇA SE PASSE */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-10 text-center">
              Comment ça se passe
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center mb-12">
              <div>
                <p className="text-foreground-600 text-base leading-relaxed">
                  Trois étapes et c&rsquo;est réglé. Le bon traitement dépend de la bonne identification : envoyez-nous une photo sur WhatsApp, on identifie l&rsquo;espèce et on vous annonce le prix en 2 h. L&rsquo;intervention est adaptée à l&rsquo;insecte, au lieu, et respecte enfants et animaux.
                </p>
              </div>
              <div className="relative">
                <div className="w-full h-64 md:h-72 rounded-xl overflow-hidden">
                  <img
                    src="/images/desinsectisation-2.webp"
                    alt="Intervention de désinsectisation professionnelle dans une pièce de vie aux tons chauds en Guadeloupe"
                    className="w-full h-full object-cover"
                   loading="lazy" decoding="async" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-camera-line text-secondary-500 text-2xl w-7 h-7 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground-950 mb-3">
                  Identification
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  Par photo WhatsApp ou sur place : le bon traitement dépend de la bonne espèce.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-flask-line text-secondary-500 text-2xl w-7 h-7 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground-950 mb-3">
                  Traitement adapté
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  Gel, pulvérisation, poudrage ou fumigation selon l&rsquo;insecte et le lieu ; produits professionnels, protocoles respectant enfants et animaux.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-shield-line text-secondary-500 text-2xl w-7 h-7 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground-950 mb-3">
                  Prévention
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  Points d&rsquo;entrée, sources d&rsquo;humidité, conseils concrets pour que ça ne revienne pas.
                </p>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* TARIFS */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Nos tarifs de désinsectisation
              </h2>
            </div>
            <div className="max-w-2xl mx-auto">
              <PriceTable rows={PRICING_ROWS} />
              <div className="text-center mt-6">
                <Link
                  to="/tarifs"
                  className="inline-flex items-center gap-2 text-secondary-500 font-semibold hover:text-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Voir tous les tarifs
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* PROFESSIONNELS */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">
                  Professionnels : restaurants, hôtels, commerces
                </h2>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center shrink-0 rounded-full bg-mango-500/20 mt-1">
                    <i className="ri-error-warning-line text-mango-500 text-lg w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <p className="text-foreground-600 text-base leading-relaxed">
                    Un insecte en salle ou en chambre coûte plus cher que dix contrats d&rsquo;entretien. Nos contrats professionnels couvrent l&rsquo;ensemble des nuisibles (insectes + rongeurs), avec registre HACCP, passages planifiés et urgences entre deux visites.
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="w-full h-64 md:h-72 rounded-xl overflow-hidden">
                  <img
                    src="/images/desinsectisation-3.webp"
                    alt="Inspection minutieuse des recoins d'une pièce lors d'une désinsectisation en Guadeloupe"
                    className="w-full h-full object-cover"
                   loading="lazy" decoding="async" />
                </div>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* FAQ */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Questions fréquentes sur la désinsectisation
              </h2>
            </div>
            <div className="max-w-3xl mx-auto">
              <FAQAccordion items={FAQ_ITEMS} />
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* CTA FINAL */}
      <CTABlock
        title="Un insecte vous gâche la vie ? Identification gratuite par photo."
      />

      {/* MAILLAGE INTERNE */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto space-y-3">
            <p className="text-center text-foreground-500 text-sm uppercase tracking-wider mb-4">À lire aussi</p>
            <Link
              to="/traitement-ravets-cafards-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Traitement ravets et cafards
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Le traitement professionnel qui les fait vraiment disparaître
              </span>
            </Link>
            <Link
              to="/punaises-de-lit-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Traitement punaises de lit
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Protocole complet, discrétion totale
              </span>
            </Link>
            <Link
              to="/scolopendres-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Traitement scolopendres
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                La barrière périmétrique qui les arrête avant la porte
              </span>
            </Link>
            <Link
              to="/demoustication-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Démoustication
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Reprenez possession de votre jardin
              </span>
            </Link>
            <Link
              to="/tarifs"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Voir tous nos tarifs
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Prix transparents, devis gratuit
              </span>
            </Link>
          </div>
        </div>
      </section>

      <ContactFormBlock source="desinsectisation" />

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}